import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";

import { useVerificationWelcomeShortcuts } from "../hooks/useVerificationWelcomeShortcuts";
import type { VerificationPurpose } from "../types/verification-api.types";
import {
  hasSeenVerificationWelcome,
  markVerificationWelcomeSeen,
  verificationWelcomeKey,
} from "../utils/verificationWelcome";
import { VerificationWelcome } from "./VerificationWelcome";

export interface OnboardingVerificationWelcomeProps {
  purpose: VerificationPurpose;
  /** The approved corporate identity; the welcome is marked as seen against it. */
  identityId: string | null;
  /** Leaves the onboarding page for `href`, re-reading the verification on the way out. */
  onNavigate: (href: string) => void;
}

/**
 * The approved result of the onboarding flow: the welcome inline, in place of a single "go home" button.
 *
 * It marks the welcome as seen, so the dashboard shell does not announce the same approval again once the
 * member leaves. Whether it celebrates is decided once, on mount: the first time this member sees this
 * approval it does; coming back to the page later it just shows the shortcuts.
 */
export function OnboardingVerificationWelcome({
  purpose,
  identityId,
  onNavigate,
}: OnboardingVerificationWelcomeProps) {
  const { t } = useTranslations("verification");
  const { user } = useAuth();
  const { shortcuts, landingPath } = useVerificationWelcomeShortcuts();
  const key = user && identityId ? verificationWelcomeKey(user.id, identityId) : null;
  const [celebrate] = useState(() => key === null || !hasSeenVerificationWelcome(key));

  useEffect(() => {
    if (key) markVerificationWelcomeSeen(key);
  }, [key]);

  // The screen's one primary action. The shortcuts are equal choices drawn as cards; the landing page is left
  // out of them (see the hook), so it is offered once, here.
  const home = landingPath ? (
    <ColocaButton
      variant="primary"
      iconTrailing={ArrowRight}
      className="w-full"
      onClick={() => onNavigate(landingPath)}
    >
      {t("result_verified_cta")}
    </ColocaButton>
  ) : null;

  return (
    <VerificationWelcome
      purpose={purpose}
      shortcuts={shortcuts}
      celebrate={celebrate}
      onSelect={(shortcut) => onNavigate(shortcut.href)}
      footer={home}
    />
  );
}
